import { create } from 'zustand';
import {
    EnergySettings,
    DEFAULT_ENERGY_SETTINGS,
    EnergyLoadResult,
    AnnualEnergyResult,
} from '../tools/analyze/energyEngine';

/** Summary produced by "Create Energy Model" (Analytical Spaces & Surfaces). */
export interface EnergyModelSummary {
    spaceCount: number;
    surfaceCount: number;
    totalArea: number;   // m² conditioned floor area
    totalVolume: number; // m³
    createdOn: number;   // epoch ms
}

/** Generic text report shown in the Analysis Results dialog. */
export interface AnalysisReport {
    title: string;
    lines: string[];
    generatedOn: number;
}

interface AnalysisState {
    /** Bumped by tools that mutate the analytical model so overlays refresh. */
    analyticalVersion: number;
    bumpAnalyticalModel: () => void;

    /** Visibility of the analytical model overlay (lines, nodes, supports). */
    showAnalyticalModel: boolean;
    toggleAnalyticalModel: () => void;

    /** Visibility of the structural load symbols overlay. */
    showLoads: boolean;
    toggleLoads: () => void;

    /** Visibility of the thermal Spaces overlay. */
    showSpaces: boolean;
    toggleSpaces: () => void;

    /** Bumped whenever a 'Structural Loads' element is added/removed. */
    loadsVersion: number;
    bumpLoads: () => void;

    /** Bumped whenever a Space/Zone/Separator element is added/removed. */
    spacesVersion: number;
    bumpSpaces: () => void;

    /** Load case name new loads are attached to (mirrors Options bar). */
    activeLoadCaseName: string;
    setActiveLoadCaseName: (name: string) => void;

    energySettings: EnergySettings;
    setEnergySettings: (patch: Partial<EnergySettings>) => void;

    energyModel: EnergyModelSummary | null;
    setEnergyModel: (summary: EnergyModelSummary | null) => void;

    lastLoads: EnergyLoadResult | null;
    setLastLoads: (result: EnergyLoadResult | null) => void;

    lastEnergy: AnnualEnergyResult | null;
    setLastEnergy: (result: AnnualEnergyResult | null) => void;

    report: AnalysisReport | null;
    setReport: (report: AnalysisReport | null) => void;

    /** Last structural analysis run (kept simple: summary values only). */
    lastStructuralRun: {
        combinationName: string;
        maxDisplacement: number; // m
        maxStress: number;       // Pa
        converged: boolean;
        message: string;
    } | null;
    setLastStructuralRun: (
        run: AnalysisState['lastStructuralRun']
    ) => void;

    /** Full FEA result set (Maps) — drives the color-fill results overlay. */
    lastFeaResults: unknown | null;
    setLastFeaResults: (results: unknown | null) => void;

    /** Member force diagram overlay (BIM "Results and Color Fill"). */
    showResults: boolean;
    toggleResults: () => void;
    setShowResults: (on: boolean) => void;

    /** True while a structural solve is in flight (drives status bar). */
    structuralAnalysisRunning: boolean;
    setStructuralAnalysisRunning: (running: boolean) => void;
}

/**
 * Central document-level state for the Analyze tab: analytical model display,
 * energy settings, energy model summary, analysis reports and results. Kept
 * separate from the BIM store because these are analysis artifacts, not
 * physical model elements, and most of them are transient per session.
 */
export const useAnalysisStore = create<AnalysisState>()((set) => ({
    analyticalVersion: 0,
    bumpAnalyticalModel: () =>
        set((state) => ({ analyticalVersion: state.analyticalVersion + 1 })),

    showAnalyticalModel: true,
    toggleAnalyticalModel: () =>
        set((state) => ({ showAnalyticalModel: !state.showAnalyticalModel })),

    showLoads: true,
    toggleLoads: () => set((state) => ({ showLoads: !state.showLoads })),

    showSpaces: true,
    toggleSpaces: () => set((state) => ({ showSpaces: !state.showSpaces })),

    loadsVersion: 0,
    bumpLoads: () => set((state) => ({ loadsVersion: state.loadsVersion + 1 })),

    spacesVersion: 0,
    bumpSpaces: () => set((state) => ({ spacesVersion: state.spacesVersion + 1 })),

    activeLoadCaseName: 'Dead',
    setActiveLoadCaseName: (name) => set({ activeLoadCaseName: name }),

    energySettings: { ...DEFAULT_ENERGY_SETTINGS },
    setEnergySettings: (patch) =>
        set((state) => ({ energySettings: { ...state.energySettings, ...patch } })),

    energyModel: null,
    setEnergyModel: (summary) => set({ energyModel: summary }),

    lastLoads: null,
    setLastLoads: (result) => set({ lastLoads: result }),

    lastEnergy: null,
    setLastEnergy: (result) => set({ lastEnergy: result }),

    report: null,
    setReport: (report) => set({ report }),

    lastStructuralRun: null,
    setLastStructuralRun: (run) => set({ lastStructuralRun: run }),

    lastFeaResults: null,
    setLastFeaResults: (results) => set({ lastFeaResults: results }),

    showResults: false,
    toggleResults: () => set((state) => ({ showResults: !state.showResults })),
    setShowResults: (on) => set({ showResults: on }),

    structuralAnalysisRunning: false,
    setStructuralAnalysisRunning: (running) => set({ structuralAnalysisRunning: running }),
}));
